"use client";

import { useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useTranslations } from "@/contexts/I18nContext";
import type { MemberProfile } from "@/lib/models";

type Tab = "info" | "medical";
type MemberKind = "adult" | "child" | "elderly_parent";

interface MemberEditModalProps {
  member: MemberProfile;
  memberId: string;
  /** Owner of the household — when true, the type chip + remove CTA are editable. */
  canEditType: boolean;
  onClose: () => void;
}

/**
 * Two-tab member edit sheet (Info + Medical), mirroring iOS
 * `Views/Members/MemberDetailView.swift` and the inline editor on
 * `MemberCard` (`MemberDashboardView.swift:189-718`).
 *
 * Captures the canonical ~15-field set:
 *   Info — name, nickname, memberType, birth m/d/y, notes
 *   Medical — allergies (csv), medications, doctor name + phone,
 *             insurance id, blood type
 *
 * Web previously only let users edit nickname + dietary restrictions
 * inline on `MemberDetail`; everything else was read-only across
 * platforms.
 */
export function MemberEditModal({
  member,
  memberId,
  canEditType,
  onClose,
}: MemberEditModalProps) {
  const t = useTranslations();

  const [tab, setTab] = useState<Tab>("info");
  // Info
  const [name, setName] = useState(member.name ?? "");
  const [nickname, setNickname] = useState(member.nickname ?? "");
  const [memberType, setMemberType] = useState<MemberKind>(
    (member.memberType as MemberKind) ?? "adult",
  );
  const [birthMonth, setBirthMonth] = useState(
    member.birthMonth ? String(member.birthMonth) : "",
  );
  const [birthDay, setBirthDay] = useState(
    member.birthDay ? String(member.birthDay) : "",
  );
  const [birthYear, setBirthYear] = useState(
    member.birthYear ? String(member.birthYear) : "",
  );
  const [notes, setNotes] = useState(member.notes ?? "");
  // Medical
  const [allergiesCsv, setAllergiesCsv] = useState(
    (member.allergies ?? []).join(", "),
  );
  const [medications, setMedications] = useState(
    member.details?.medications ?? "",
  );
  const [doctorName, setDoctorName] = useState(
    member.details?.doctor_name ?? "",
  );
  const [doctorPhone, setDoctorPhone] = useState(
    member.details?.doctor_phone ?? "",
  );
  const [insuranceId, setInsuranceId] = useState(
    member.details?.insurance_id ?? "",
  );
  const [bloodType, setBloodType] = useState(
    member.details?.blood_type ?? "",
  );

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const ref = doc(db, "member_profiles", memberId);
      const allergies = allergiesCsv
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      // Merge into the existing flexible-detail map so we don't blow
      // away any keys other platforms rely on.
      const details: Record<string, string> = { ...(member.details ?? {}) };
      writeOrDelete(details, "medications", medications);
      writeOrDelete(details, "doctor_name", doctorName);
      writeOrDelete(details, "doctor_phone", doctorPhone);
      writeOrDelete(details, "insurance_id", insuranceId);
      writeOrDelete(details, "blood_type", bloodType);

      await updateDoc(ref, {
        name: name.trim(),
        nickname: nickname.trim() || null,
        ...(canEditType ? { memberType } : {}),
        birthMonth: parseIntOrNull(birthMonth),
        birthDay: parseIntOrNull(birthDay),
        birthYear: parseIntOrNull(birthYear),
        notes: notes.trim() || null,
        allergies,
        details,
      });

      onClose();
    } catch (err) {
      console.error("Failed to save member edits:", err);
      setError(t("members.edit.errorGeneric"));
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full sm:max-w-md bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden text-gray-900 dark:text-gray-100 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-white/10">
          <button
            type="button"
            onClick={onClose}
            className="text-blue-500 text-sm font-medium"
          >
            {t("common.cancel")}
          </button>
          <h2 className="font-bold text-base">{t("members.edit.title")}</h2>
          <button
            type="button"
            onClick={handleSave}
            disabled={submitting || !name.trim()}
            className="text-sm font-semibold text-[color:var(--accent-color)] disabled:opacity-50"
          >
            {submitting ? t("members.edit.saving") : t("members.edit.save")}
          </button>
        </div>

        {/* Tab strip */}
        <div className="px-4 pt-3">
          <div className="flex gap-1 p-1 rounded-lg bg-gray-100 dark:bg-white/5 text-sm">
            <TabButton current={tab} value="info" onClick={setTab}>
              {t("members.edit.tab.info")}
            </TabButton>
            <TabButton current={tab} value="medical" onClick={setTab}>
              {t("members.edit.tab.medical")}
            </TabButton>
          </div>
        </div>

        <form onSubmit={handleSave} className="p-4 space-y-4">
          {tab === "info" ? (
            <>
              <Field label={t("members.edit.nameLabel")}>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="form-input"
                />
              </Field>
              <Field label={t("members.edit.nicknameLabel")}>
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  className="form-input"
                />
              </Field>
              {canEditType && (
                <Field label={t("members.edit.memberTypeLabel")}>
                  <div className="flex gap-2">
                    {(["adult", "child", "elderly_parent"] as const).map((value) => {
                      const active = memberType === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setMemberType(value)}
                          className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                            active
                              ? "bg-[color:var(--accent-color,#00C7BE)] text-white border-[color:var(--accent-color,#00C7BE)]"
                              : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-700"
                          }`}
                        >
                          {t(`register.memberType.${value}`)}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              )}
              <Field label={t("members.edit.birthdayLabel")}>
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="number"
                    inputMode="numeric"
                    min="1"
                    max="12"
                    value={birthMonth}
                    onChange={(e) => setBirthMonth(e.target.value)}
                    placeholder={t("members.add.birthMonthPlaceholder")}
                    className="form-input text-center"
                  />
                  <input
                    type="number"
                    inputMode="numeric"
                    min="1"
                    max="31"
                    value={birthDay}
                    onChange={(e) => setBirthDay(e.target.value)}
                    placeholder={t("members.add.birthDayPlaceholder")}
                    className="form-input text-center"
                  />
                  <input
                    type="number"
                    inputMode="numeric"
                    min="1900"
                    max="2100"
                    value={birthYear}
                    onChange={(e) => setBirthYear(e.target.value)}
                    placeholder={t("members.add.birthYearPlaceholder")}
                    className="form-input text-center"
                  />
                </div>
              </Field>
              <Field label={t("members.edit.notesLabel")}>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="form-input resize-none"
                />
              </Field>
            </>
          ) : (
            <>
              <Field label={t("members.edit.allergiesLabel")}>
                <input
                  type="text"
                  value={allergiesCsv}
                  onChange={(e) => setAllergiesCsv(e.target.value)}
                  placeholder={t("members.edit.allergiesPlaceholder")}
                  className="form-input"
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  {t("members.edit.allergiesHelp")}
                </p>
              </Field>
              <Field label={t("members.edit.medicationsLabel")}>
                <textarea
                  value={medications}
                  onChange={(e) => setMedications(e.target.value)}
                  rows={2}
                  className="form-input resize-none"
                />
              </Field>
              <Field label={t("members.edit.doctorNameLabel")}>
                <input
                  type="text"
                  value={doctorName}
                  onChange={(e) => setDoctorName(e.target.value)}
                  className="form-input"
                />
              </Field>
              <Field label={t("members.edit.doctorPhoneLabel")}>
                <input
                  type="tel"
                  value={doctorPhone}
                  onChange={(e) => setDoctorPhone(e.target.value)}
                  className="form-input"
                />
              </Field>
              <Field label={t("members.edit.insuranceIdLabel")}>
                <input
                  type="text"
                  value={insuranceId}
                  onChange={(e) => setInsuranceId(e.target.value)}
                  className="form-input"
                />
              </Field>
              <Field label={t("members.edit.bloodTypeLabel")}>
                <input
                  type="text"
                  value={bloodType}
                  onChange={(e) => setBloodType(e.target.value)}
                  placeholder="A+ / O-"
                  className="form-input"
                />
              </Field>
            </>
          )}

          {error && <p className="text-sm text-red-500">{error}</p>}
        </form>
      </div>

      <style>{`
        .form-input {
          width: 100%;
          padding: 0.5rem 0.75rem;
          border-radius: 0.5rem;
          background-color: rgb(243 244 246);
          outline: none;
        }
        .form-input:focus {
          box-shadow: 0 0 0 2px var(--accent-color, #00C7BE);
        }
        .dark .form-input {
          background-color: rgba(255, 255, 255, 0.1);
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function TabButton({
  current,
  value,
  onClick,
  children,
}: {
  current: Tab;
  value: Tab;
  onClick: (t: Tab) => void;
  children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={`flex-1 rounded-md py-1.5 transition-colors ${
        active
          ? "bg-white dark:bg-gray-700 shadow-sm font-semibold"
          : "text-gray-500 dark:text-gray-400"
      }`}
    >
      {children}
    </button>
  );
}

function parseIntOrNull(value: string): number | null {
  const v = value.trim();
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function writeOrDelete(map: Record<string, string>, key: string, value: string) {
  const trimmed = value.trim();
  if (trimmed) {
    map[key] = trimmed;
  } else {
    delete map[key];
  }
}
